import { Check } from 'lucide-react';
import { Fragment, type ReactElement } from 'react';

import { cn } from '@/lib/utils';

interface StepperProps {
  readonly steps: readonly string[];
  readonly currentStep: number;
  readonly 'aria-label'?: string;
}

export function Stepper({ steps, currentStep, 'aria-label': ariaLabel }: StepperProps): ReactElement {
  return (
    <div role="navigation" aria-label={ariaLabel} className="flex items-start">
      {steps.map((label, idx) => {
        const isCompleted = idx < currentStep;
        const isCurrent = idx === currentStep;

        return (
          <Fragment key={label}>
            <div className="flex flex-col items-center gap-1.5 min-w-0">
              <div
                aria-current={isCurrent ? 'step' : undefined}
                aria-label={label}
                className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-colors flex-shrink-0',
                  isCurrent || isCompleted
                    ? 'bg-brand text-brand-foreground'
                    : 'bg-gray-200 text-gray-600',
                )}
              >
                {isCompleted
                  ? <Check className="h-4 w-4" aria-hidden="true" />
                  : idx + 1
                }
              </div>
              <span
                className={cn(
                  'text-center leading-tight w-20',
                  isCurrent
                    ? 'text-xs font-bold text-brand'
                    : 'text-xs text-muted-foreground',
                )}
              >
                {label}
              </span>
            </div>

            {idx < steps.length - 1 && (
              <div
                className={cn(
                  'flex-1 h-0.5 mt-4 transition-colors',
                  isCompleted ? 'bg-brand' : 'bg-gray-200',
                )}
              />
            )}
          </Fragment>
        );
      })}
    </div>
  );
}
